// =========================================================
// Vojjin Cookies Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Easing {
    // 🧭 Linear
    static linear = (t) => t;

    // 🟩 Quadratic
    static easeInQuad = (t) => t * t;
    static easeOutQuad = (t) => t * (2 - t);
    static easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    // 🟦 Cubic
    static easeInCubic = (t) => t * t * t;
    static easeOutCubic = (t) => (--t) * t * t + 1;
    static easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;

    // 🟨 Quartic
    static easeInQuart = (t) => t * t * t * t;
    static easeOutQuart = (t) => 1 - (--t) * t * t * t;
    static easeInOutQuart = (t) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;

    // 🟥 Quintic
    static easeInQuint = (t) => t * t * t * t * t;
    static easeOutQuint = (t) => 1 + (--t) * t * t * t * t;
    static easeInOutQuint = (t) => t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;

    // ⚪ Sine
    static easeInSine = (t) => 1 - Math.cos((t * Math.PI) / 2);
    static easeOutSine = (t) => Math.sin((t * Math.PI) / 2);
    static easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

    // 🟠 Exponential
    static easeInExpo = (t) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1)));
    static easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    static easeInOutExpo = (t) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        return t < 0.5
            ? Math.pow(2, 20 * t - 10) / 2
            : (2 - Math.pow(2, -20 * t + 10)) / 2;
    };

    // 🌀 Circular
    static easeInCirc = (t) => 1 - Math.sqrt(1 - t * t);
    static easeOutCirc = (t) => Math.sqrt(1 - (--t) * t);
    static easeInOutCirc = (t) => t < 0.5
        ? (1 - Math.sqrt(1 - 4 * t * t)) / 2
        : (Math.sqrt(1 - (2 * t - 2) * (2 * t - 2)) + 1) / 2;

    // 🪶 Back
    static easeInBack = (t) => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return c3 * t * t * t - c1 * t * t;
    };
    static easeOutBack = (t) => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };
    static easeInOutBack = (t) => {
        const c1 = 1.70158;
        const c2 = c1 * 1.525;
        return t < 0.5
            ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
            : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
    };

    // 🪗 Elastic
    static easeInElastic = (t) => {
        const c4 = (2 * Math.PI) / 3;
        return t === 0
            ? 0
            : t === 1
                ? 1
                : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * c4);
    };
    static easeOutElastic = (t) => {
        const c4 = (2 * Math.PI) / 3;
        return t === 0
            ? 0
            : t === 1
                ? 1
                : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    };
    static easeInOutElastic = (t) => {
        const c5 = (2 * Math.PI) / 4.5;
        return t === 0
            ? 0
            : t === 1
                ? 1
                : t < 0.5
                    ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * c5)) / 2
                    : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * c5)) / 2 + 1;
    };

    // 🏀 Bounce
    static easeOutBounce = (t) => {
        const n1 = 7.5625;
        const d1 = 2.75;
        if (t < 1 / d1) return n1 * t * t;
        else if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
        else if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
        else return n1 * (t -= 2.625 / d1) * t + 0.984375;
    };
    static easeInBounce = (t) => 1 - Easing.easeOutBounce(1 - t);
    static easeInOutBounce = (t) => t < 0.5
        ? (1 - Easing.easeOutBounce(1 - 2 * t)) / 2
        : (1 + Easing.easeOutBounce(2 * t - 1)) / 2;

    // ⚡ Optional smooth & soft variants
    static easeOutBackSoft = (t) => {
        const c1 = 1.10158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };

    static easeInOutSmoothStep = (t) => t * t * (3 - 2 * t);

    static easeInOutSigmoid = (t) => 1 / (1 + Math.exp(-12 * (t - 0.5)));

    static easeInOutStep = (t, steps = 5) => Math.floor(t * steps) / steps;

    static easeOutOvershoot = (t) => {
        const s = 1.70158 * 1.5;
        return 1 + s * Math.pow(t - 1, 2) * (1 + s * (t - 1));
    };

    // 🔍 Lookup helper
    static get(name = "linear") {
        return Easing[name] || Easing.linear;
    }


}

registerGlobal("Easing", Easing);
